import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Store, MapPin, Share2, ExternalLink, Briefcase, Check, Globe, ClipboardList } from "lucide-react";
import DirectionsShareButton, { directionsLink } from "@/components/maps/DirectionsShareButton";
import { ShopLinkTools } from "@/components/vendor/ShopLinkTools";
import { ProfileCompleteness, completenessItems } from "@/components/vendor/ProfileCompleteness";
import { LogoNudge, logoNudgeVisible } from "@/components/vendor/LogoNudge";
import { T } from "@/i18n";

const btn = "inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 min-h-[44px] text-sm font-semibold whitespace-nowrap transition-colors min-w-0";

function ShopPicker({ shops, activeShop, activeShopId, setActiveShopId, hasLoc }) {
  return (
    <div className="mt-4 flex items-center gap-2 flex-wrap min-w-0" data-testid="active-shop-row">
      <span className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center flex-shrink-0"><Store className="w-4 h-4" /></span>
      {shops.length > 1 ? (
        <div className="flex items-center border border-slate-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:border-emerald-500 min-w-0 flex-1 sm:flex-none" data-testid="active-shop-selector">
          <select value={activeShopId || ""} onChange={(e) => setActiveShopId(e.target.value)} aria-label="Choose shop" className="bg-transparent outline-none text-sm font-bold pr-2 min-w-0 flex-1 sm:max-w-xs truncate">
            {shops.map((sh) => <option key={sh.shop_id} value={sh.shop_id}>{sh.name}</option>)}
          </select>
        </div>
      ) : (
        <span className="font-display text-lg font-bold text-slate-900 truncate min-w-0" data-testid="active-shop-name">{activeShop.name}</span>
      )}
      {hasLoc && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-1 text-xs font-semibold select-none" data-testid="location-set-badge">
          <Check className="w-3.5 h-3.5" /> Location set
        </span>
      )}
    </div>
  );
}

function PublicPageCard({ shop, onShare }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 min-w-0 max-w-full overflow-hidden" data-testid="public-profile-box">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0"><Globe className="w-4 h-4" /></span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">Public Profile</div>
          <div className="text-xs text-slate-500">Customers yahi page dekhte hain</div>
        </div>
      </div>
      <div className="[&>div]:mb-3"><ShopLinkTools shop={shop} /></div>
      <div className="grid grid-cols-2 gap-2 min-w-0" data-testid="vendor-public-actions">
        <Link to={`/v/${shop.slug || shop.shop_id}`} target="_blank" rel="noopener noreferrer" className={`${btn} border border-emerald-200 text-emerald-800 bg-white hover:bg-emerald-50`} data-testid="view-public-profile-btn">
          <ExternalLink className="w-4 h-4" /> Preview
        </Link>
        <button onClick={onShare} className={`${btn} bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm`} data-testid="share-shop-btn">
          <Share2 className="w-4 h-4" /> Share
        </button>
        <DirectionsShareButton shopName={shop.name} url={directionsLink(shop.latitude, shop.longitude)} missingText="Pehle Shop Profile me map location set karein, phir directions bhej sakte hain." className="col-span-2 justify-center min-h-[44px]" testid="dashboard-directions-share-btn" />
      </div>
    </div>
  );
}

function TodoCard({ shop, hasLoc, onSetLocation, openShopField }) {
  const [, rerender] = useState(0);
  const logoRow = logoNudgeVisible(shop);
  const skip = [!hasLoc && "location", logoRow && "logo"].filter(Boolean);
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 min-w-0 space-y-3" data-testid="vendor-todo-card">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0"><ClipboardList className="w-4 h-4" /></span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">Zaroori kaam</div>
          <div className="text-xs text-slate-500">Yeh poora karein, customers ko aapki dukaan aasani se milegi</div>
        </div>
      </div>
      {!hasLoc && (
        <div className="flex flex-col min-[420px]:flex-row min-[420px]:items-center gap-3 rounded-xl border border-amber-300 bg-white px-3 py-3" data-testid="location-reminder">
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold text-amber-900 break-words">Dukaan ki sahi location set karein</div>
            <div className="text-xs text-amber-800 mt-0.5 break-words">Customers ko Directions milega aur dukaan aas-paas ke results mein dikhegi.</div>
          </div>
          <button onClick={onSetLocation} className={`${btn} bg-amber-500 hover:bg-amber-600 text-slate-900 flex-shrink-0`} data-testid="location-reminder-btn">
            <MapPin className="w-4 h-4" /> Set location
          </button>
        </div>
      )}
      <LogoNudge shop={shop} onUpload={() => openShopField("shop-logo-section")} onLater={() => rerender((x) => x + 1)} />
      <ProfileCompleteness shop={shop} hasLoc={hasLoc} onOpen={openShopField} skip={skip} />
    </div>
  );
}

// Vendor Dashboard top: greeting + Tool Bag, shop picker, public page card and one grouped to-do card.
export function DashboardTop({ user, shops, activeShop, activeShopId, setActiveShopId, hasLoc, onToolBag, onShare, onSetLocation, openShopField }) {
  const pending = activeShop && completenessItems(activeShop, hasLoc).some((i) => !i.done);
  return (
    <section className="rounded-3xl border border-emerald-100 bg-white shadow-sm mb-5 overflow-hidden" data-testid="vendor-hero-card">
      <div className="bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 p-4 sm:p-6 border-b border-emerald-100/70">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-1 mb-2" data-testid="vendor-mode-badge"><Store className="w-3.5 h-3.5" /> Vendor Mode</div>
            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight break-words">Namaste, {user.name.split(" ")[0]}!</h1>
            <p className="text-sm text-slate-600 mt-1" data-testid="vendor-hero-sub">Apni dukaan, products, services aur ads yahan sambhaalein.</p>
          </div>
          <button onClick={onToolBag} className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 px-3 sm:px-4 py-2 min-h-[44px] text-sm font-semibold shadow-sm shadow-amber-500/20 transition-colors" data-testid="open-toolbag-btn">
            <Briefcase className="w-4 h-4" /> <T k="nav.toolbag" />
          </button>
        </div>
        {activeShop && <ShopPicker shops={shops} activeShop={activeShop} activeShopId={activeShopId} setActiveShopId={setActiveShopId} hasLoc={hasLoc} />}
      </div>
      {activeShop && (
        <div className={`p-3 sm:p-6 grid gap-3 sm:gap-4 min-w-0 lg:items-start ${pending ? "lg:grid-cols-2" : ""}`}>
          <PublicPageCard shop={activeShop} onShare={onShare} />
          {pending && <TodoCard shop={activeShop} hasLoc={hasLoc} onSetLocation={onSetLocation} openShopField={openShopField} />}
        </div>
      )}
    </section>
  );
}
